import React from "react";
import { Link } from "react-router-dom";
import { FiCode, FiCpu, FiUsers, FiHeart, FiClock, FiArrowRight } from "react-icons/fi";

// The look of each category: used for the picture shown until a thumbnail is uploaded, and for the category labels.
export const CATEGORY_THEME = {
  "it-services": { from: "#0b5ed7", to: "#22d3ee", Icon: FiCode, label: "IT Services", page: "blogs-it-services" },
  "ai-tools": { from: "#6d28d9", to: "#22d3ee", Icon: FiCpu, label: "AI Tools", page: "blogs-ai-tools" },
  "cowork-marketplace": { from: "#0369a1", to: "#10b981", Icon: FiUsers, label: "CoWork Marketplace", page: "blogs-cowork-marketplace" },
  foundation: { from: "#be185d", to: "#f59e0b", Icon: FiHeart, label: "Foundation", page: "blogs-foundation" },
};

export function formatDate(iso) {
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); } catch { return ""; }
}

// The post's picture, or - until one is uploaded in the admin panel - a branded picture of its category.
export function BlogThumb({ post, className = "", eager = false }) {
  const theme = CATEGORY_THEME[post.category] || CATEGORY_THEME["it-services"];
  if (post.thumbnail) {
    return <img src={post.thumbnail} alt={post.thumbnail_alt || post.title} loading={eager ? "eager" : "lazy"} className={`w-full h-full object-cover ${className}`} />;
  }
  const { Icon } = theme;
  return (
    <div className={`relative w-full h-full overflow-hidden ${className}`} style={{ background: `linear-gradient(135deg, ${theme.from}, ${theme.to})` }} role="img" aria-label={post.thumbnail_alt || post.title}>
      <div className="absolute inset-0 opacity-30" style={{ background: "radial-gradient(circle at 20% 20%, rgba(255,255,255,.55), transparent 45%), radial-gradient(circle at 85% 90%, rgba(0,0,0,.45), transparent 50%)" }} />
      <Icon className="absolute -right-4 -bottom-6 text-white/20" size={150} aria-hidden="true" />
      <div className="absolute left-4 bottom-4 right-24">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/30 text-white text-[11px] font-semibold uppercase tracking-wider"><Icon size={12} /> {theme.label}</span>
      </div>
    </div>
  );
}

export function CategoryPill({ category, className = "" }) {
  const theme = CATEGORY_THEME[category];
  if (!theme) return null;
  return <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider text-white ${className}`} style={{ background: `linear-gradient(90deg, ${theme.from}, ${theme.to})` }}>{theme.label}</span>;
}

export default function BlogCard({ post }) {
  return (
    <article className="group rounded-2xl overflow-hidden bg-white/5 border border-navy-border hover:border-accent-light/60 hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(34,211,238,0.12)] transition-all duration-300 flex flex-col">
      <Link to={`/blog/${post.slug}`} className="block aspect-[16/9] overflow-hidden bg-navy-dark" aria-label={post.title} tabIndex={-1}>
        <BlogThumb post={post} className="group-hover:scale-105 transition-transform duration-500" />
      </Link>
      <div className="p-5 flex flex-col flex-1">
        <div className="mb-3">
          <CategoryPill category={post.category} />
        </div>
        <h3 className="text-lg font-bold leading-snug text-white group-hover:text-accent-light transition-colors">
          <Link to={`/blog/${post.slug}`}>{post.title}</Link>
        </h3>
        <p className="text-sm text-gray-400 leading-relaxed mt-2 line-clamp-3">{post.excerpt}</p>
        <div className="mt-auto pt-4 flex items-center justify-between text-xs text-gray-500">
          <span className="flex items-center gap-1.5"><FiClock size={12} /> {post.reading_minutes} min read &middot; {formatDate(post.published_at)}</span>
          <Link to={`/blog/${post.slug}`} className="inline-flex items-center gap-1 text-accent-light font-semibold group-hover:gap-2 transition-all">Read <FiArrowRight size={13} /></Link>
        </div>
      </div>
    </article>
  );
}
